// Supermarket.js
import React, { useEffect, useState, useRef } from 'react';
import './Supermarket.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import data from './Data.json';

function getGroupBySlug(slug) {
  return Object.keys(data).find(
    (key) =>
      (data[key].slug && data[key].slug === slug) ||
      key.toLowerCase() === slug?.toLowerCase()
  );
}

function Supermarket() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlSlug = searchParams.get('group');
  const selectedGroup = urlSlug ? getGroupBySlug(urlSlug) || 'supermarkets' : 'supermarkets';
  const groupData = data[selectedGroup];
  const bgClasses = ["bg1", "bg2", "bg3", "bg4"];

  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
  const sidebarRef = useRef(null);
  const menuIconRef = useRef(null);

  // --- Popup logic ---
  const [showPopup, setShowPopup] = useState(false);

  // Ref for the hold timer ID
  const holdTimer = useRef(null);
  const HOLD_DURATION_MS = 5000; // 5 seconds

  const handleGroupClick = (group) => {
    const slug = data[group].slug || group;
    setSearchParams({ group: slug });
    navigate(`/Industrial-specification?group=${slug}`);
    if (window.innerWidth <= 768) {
      setShowSidebar(false);
    }
  };

  // Effect for keyboard event listeners (Ctrl + F9 to open) - Remains the same
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'F9') {
        e.preventDefault();
        setShowPopup(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Effect for hold-to-open functionality on mobile
  useEffect(() => {
    const handleTouchStart = () => {
      // Check if it's a mobile device screen width
      if (window.innerWidth <= 768) {
        // Clear any existing timer to prevent multiple timers from running
        if (holdTimer.current) {
          clearTimeout(holdTimer.current);
        }
        // Start a new timer for the hold duration
        holdTimer.current = setTimeout(() => {
          setShowPopup(true); // Show the popup when hold duration is met
          holdTimer.current = null; // Clear the timer ID as it has fired
        }, HOLD_DURATION_MS);
      }
    };

    const handleTouchEnd = () => {
      // Clear the timer if the touch ends before the hold duration is met
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
    };

    const handleTouchCancel = () => {
      // Clear the timer if the touch is interrupted (e.g., finger moves too much)
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchCancel);

    // Clean up event listeners and any lingering timer on component unmount
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
      }
    };
  }, []); // Empty dependency array: runs once on mount for listeners

  // Effect to handle clicks outside the sidebar to close it on mobile - Remains the same
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSidebar &&
        window.innerWidth <= 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuIconRef.current &&
        !menuIconRef.current.contains(event.target)
      ) {
        setShowSidebar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSidebar]);

  // Effect to handle window resize for sidebar visibility - Remains the same
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && !showSidebar) {
        setShowSidebar(true);
      } else if (window.innerWidth <= 768 && showSidebar) {
        setShowSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showSidebar]);

  const handleCopyLink = (group) => {
    const slug = data[group].slug || group;
    const fullUrl = `${window.location.origin}/Industrial-specification?group=${slug}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      alert(`Copied link: ${fullUrl}`);
      setShowPopup(false);
    });
  };

  if (!groupData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="supermarket-layout">
      {/* Menu icon for mobile */}
      <div className="menu-icon-container">
        <button
          className="menu-icon"
          onClick={() => setShowSidebar(!showSidebar)}
          ref={menuIconRef}
        >
          {showSidebar ? '✕' : '->'}
        </button>
      </div>

      <aside
        ref={sidebarRef}
        className={`sidebar-group-select ${showSidebar ? 'open' : ''}`}
      >
        <h3 style={{ marginBottom: 18, fontWeight: 700, color: '#1976d2', fontSize: 22 }}>Categories</h3>
        <div style={{ flex: 1, width: '100%', overflowY: 'auto', padding: '0 10px' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {Object.keys(data).map((group) => (
              <li key={group} style={{ marginBottom: 12 }}>
                <button
                  style={{
                    background: selectedGroup === group ? 'linear-gradient(90deg, #1976d2 0%, #43a047 100%)' : '#fff',
                    color: selectedGroup === group ? '#fff' : '#1976d2',
                    border: selectedGroup === group ? 'none' : '1px solid #1976d2',
                    borderRadius: 8,
                    padding: '10px 18px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    width: '100%',
                    fontSize: 16,
                    boxShadow: selectedGroup === group ? '0 2px 8px rgba(25,118,210,0.10)' : 'none',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => handleGroupClick(group)}
                >
                  {group.charAt(0).toUpperCase() + group.slice(1)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <div className="main-content-area">
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
          <section className="about-section-supermarket upgraded-about-section">
            <div className="about-content-flex no-image-flex">
              <div className="about-text-block full-width-block">
                <h1
                  className="about-title-modern"
                  style={{
                    fontSize: '2.5rem',
                    background: 'linear-gradient(90deg, #1976d2 0%, #43a047 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 900,
                    letterSpacing: '1px',
                    marginBottom: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '18px',
                    textShadow: '0 2px 8px rgba(25,118,210,0.10)',
                  }}
                >
                  <span className="about-logo-circle">
                    <img src="/images/auditlogo.png" alt="Audit Logo" className="about-logo-img" />
                  </span>
                  <span>{groupData.about.title}</span>
                </h1>

                <p className="about-desc-supermarket about-desc-modern">{groupData.about.description}</p>
                <ul className="about-highlights-supermarket about-highlights-modern">
                  {groupData.about.highlights.map((item, idx) => (
                    <li key={idx} className="about-highlight-item-modern">
                      <span className="about-highlight-icon">✔</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="features-section-supermarket">
            <h2>Why Choose Auditus-iERP for Your {selectedGroup.charAt(0).toUpperCase() + selectedGroup.slice(1)}?</h2>
            <div className="features-list-supermarket">
              {groupData.features.map((feature, idx) => (
                <div
                  className={`feature-card-supermarket bg${(idx % bgClasses.length) + 1}`}
                  key={feature.title}
                >
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <ul>
                    {feature.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Popup for Ctrl+F9 / 5-second hold on mobile */}
      {showPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowPopup(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 12,
              minWidth: 320,
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: 16 }}>Copy Group Link</h2>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.keys(data).map((group) => (
                <li key={group} style={{ marginBottom: 12 }}>
                  <button
                    onClick={() => handleCopyLink(group)}
                    style={{
                      padding: '10px 14px',
                      background: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    {group.charAt(0).toUpperCase() + group.slice(1)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default Supermarket;